import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloError } from '@apollo/client';
import Grid from '@mui/material/Grid';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import LaunchIcon from '@mui/icons-material/Launch';
import Divider from '@mui/material/Divider';
import useForm from '../../../../components/_hook/useForm';
import useLanguage from '../../../../components/_hook/useLanguage';
import { useUpdateProductDescriptionMutation } from '../../../../../graphql/mutation/product/updateProductDescriptionMutation';
import useNotification from '../../../../components/_hook/useNotification';
import useRouter from '../../../../components/_hook/useRouter';
import { useProductQuery } from '../../../../../graphql/query/product/useProductQuery';
import NotFound from '../../../../components/_error/NotFound';
import { ITranslationSubTypeInput } from '../../../../../graphql/type/_inputTypes/translation';
import Context from '../../../../context';
import useRoutes from '../../../../components/_hook/useRoutes';
import ProductLayout from '../../../../layout/Product';
import StickyFormFooter from '../../../../components/_form/StickyFormFooter';
import useKeepitalRoutes from '../../../../components/_hook/useKeepitalRoutes';
import Helmet from '../../../../components/Helmet';
import InputTranslationRichtext from '../../../../components/_input/InputTranslation/InputTranslationRichtext';
import ProductDetailDropshippedProductInfo from '../_module/ProductDetailDropshippedProductInfo';

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  paper: {
    margin: theme.spacing(1),
    padding: theme.spacing(3),
    width: '100%',
  },
}));

function ProductDetailDescription() {
  interface IFormValue {
    description: ITranslationSubTypeInput[];
    uploadedDescriptionImageIds: string[];
  }

  const { t } = useTranslation();
  const classes = useStyles();
  const { notification } = useNotification();
  const { params, history } = useRouter();
  const { path } = useRoutes();
  const { company } = useContext(Context);
  const { docID: productId } = params;
  const { getText } = useLanguage();
  const { path: keepitalPath } = useKeepitalRoutes();
  const productFragment = `
            _id
            status
            slug
            featured
            isDropshippedProduct
            name {
              la
              text
            }
            infos {
              desc {
                la
                text
              }
            }
  `;
  const { data, loading } = useProductQuery(productFragment, {
    variables: {
      filter: {
        companyId: company._id,
        _id: productId as string,
      },
    },
    skip: productId === 'new',
  });

  function onCompleted() {
    notification.success(t('done'));
  }

  function onError(e: ApolloError) {
    notification.error(e.message);
  }

  const [
    updateProductDescriptionMutation,
    { loading: isUpdatingProduct },
  ] = useUpdateProductDescriptionMutation(
    `updatedProduct {
				${productFragment}
			}`,
    {
      onCompleted,
      onError,
    }
  );

  async function onSubmitForm(tempValues: IFormValue) {
    if (data) {
      await updateProductDescriptionMutation({
        variables: {
          _id: data.products.items[0]._id,
          description: tempValues.description,
          uploadedDescriptionImageIds: tempValues.uploadedDescriptionImageIds,
        },
      });
    }
  }

  const { values, setValue, handleSubmit, setValues } = useForm<IFormValue>({
    defaultValues: {
      description: [],
      uploadedDescriptionImageIds: [],
    },
    onSubmit: onSubmitForm,
  });

  useEffect(() => {
    if (!loading && data && data.products.items.length) {
      const product = data.products.items[0];

      setValues({
        description:
          product.infos && product.infos.desc
            ? product.infos.desc.map((label) => ({
                text: label.text,
                la: label.la,
              }))
            : [],
        uploadedDescriptionImageIds: [],
      });
    }
  }, [loading]);

  if (productId !== 'new' && loading && productId) {
    return (
      <Grid container justifyContent="center">
        <CircularProgress size={30} color="inherit" />
      </Grid>
    );
  }

  const product = productId && data ? data.products.items[0] : null;

  if (!product) {
    return <NotFound />;
  }

  if (product && product.isDropshippedProduct) {
    return <ProductDetailDropshippedProductInfo />;
  }

  return (
    <Paper square className={classes.paper}>
      <Helmet title={`${t('description')} | ${t('product')}`} />
      <Grid container spacing={2}>
        <Grid container justifyContent="space-between" spacing={1}>
          <Grid item>
            <Typography variant="h6">
              {t('update')} {getText(product.name)} {t('description')}
            </Typography>
          </Grid>
          {product && (
            <Grid item>
              <Button
                size="small"
                color="primary"
                variant="contained"
                startIcon={<LaunchIcon />}
                component="a"
                href={keepitalPath('company.showcase.product.index', {
                  origin: company.origin ? company.origin.toLowerCase() : '',
                  companySlug: company.slug ? company.slug : '',
                  productSlug: product.slug,
                })}
                target="_blank"
              >
                {t('view')}
              </Button>
            </Grid>
          )}
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Grid>
        <Grid container item xs={12}>
          <Grid item xs={12}>
            <InputTranslationRichtext
              label={t('description')}
              values={values.description}
              enableUploadImage
              onUploadedImage={(uploadedImages) =>
                setValue(
                  'uploadedDescriptionImageIds',
                  uploadedImages.map((uploadedImage) => uploadedImage._id)
                )
              }
              onChange={(tempValues) => setValue('description', tempValues)}
            />
          </Grid>
        </Grid>
        <StickyFormFooter
          onCancel={() =>
            history.push(
              path('product.list', {
                companyID: company._id,
              })
            )
          }
          onSubmit={handleSubmit}
          isSubmitting={isUpdatingProduct}
        />
      </Grid>
    </Paper>
  );
}

export default () => <ProductLayout main={<ProductDetailDescription />} />;
